/**
 * Integrytas Group - Base App (MOBILE)
 * Page: MTA.TASKS — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/MtaTasksPage.tsx: a personal task
 * inbox. A DISTINCT presentation (dense card list + status facet chips +
 * bottom-sheet detail) over the SAME data/logic layer — reads via apiDb
 * (mta_task, filtered to the caller's own tasks by RLS + assignee), and every
 * status change routes through the Common Routine (routedUpdate) — §2 single
 * write path. The desktop page issues a direct apiDb.update; on mobile the write
 * is funnelled through routed_writes so it carries forensic stamping, the audit
 * ledger and PHE hooks. Never the desktop grid re-hosted; §16.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, X, Check, Play, Ban, ClipboardList } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useActiveLocale } from '../../lib/use_active_locale';
import { useFormatDate, useFormatDateTime } from '../../lib/format_date';
import { routedUpdate } from '../../lib/routed_writes';
import { MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill } from '../components';
import type { MobileLeadTone, MobilePillTone } from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'MTA.TASKS';

type TaskStatus = 'OPEN' | 'IN_PROGRESS' | 'DONE' | 'CANCELLED';

// Task status (CHECK enum) → localized label + status pill + lead accent (§17.B11).
// The stored value stays the code; only the label is translated.
const STATUS_META: Record<TaskStatus, { labelKey: string; label: string; pill: MobilePillTone; lead: MobileLeadTone }> = {
  OPEN:        { labelKey: 'mobile.mta_tasks.status.OPEN',        label: 'Open',        pill: 'info', lead: 'blue' },
  IN_PROGRESS: { labelKey: 'mobile.mta_tasks.status.IN_PROGRESS', label: 'In progress', pill: 'wait', lead: 'amber' },
  DONE:        { labelKey: 'mobile.mta_tasks.status.DONE',        label: 'Done',        pill: 'ok',   lead: 'green' },
  CANCELLED:   { labelKey: 'mobile.mta_tasks.status.CANCELLED',   label: 'Cancelled',   pill: 'no',   lead: 'slate' },
};

const STATUS_ORDER: TaskStatus[] = ['OPEN', 'IN_PROGRESS', 'DONE', 'CANCELLED'];

type StatusFacet = 'all' | TaskStatus;

interface MobileTaskRow {
  mta_task_id: number;
  title: string;
  body: string | null;
  entity_name: string | null;
  record_id: string | null;
  status: TaskStatus;
  due_date: string | null;
  created_at: string;
}

export function MobileMtaTasksPage({ session }: MobilePageComponentProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  // Task labels/dates follow the active locale; re-fetch when it changes so the
  // formatted due/created dates and status labels stay in step.
  const activeLocale = useActiveLocale();
  const fmtDate = useFormatDate();
  const fmtDateTime = useFormatDateTime();
  const myId = session.effective_user.user_id;

  const [tasks, setTasks] = useState<MobileTaskRow[]>([]);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('all');
  const [selected, setSelected] = useState<MobileTaskRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      // RLS scopes rows to the caller; the explicit assignee filter mirrors the
      // desktop "my tasks" inbox intent.
      const { data, error: e } = await apiDb
        .from('mta_task')
        .select('mta_task_id, title, body, entity_name, record_id, status, due_date, created_at')
        .eq('assignee_user_id', myId)
        .order('created_at', { ascending: false });
      if (e) throw e;
      setTasks((data ?? []) as MobileTaskRow[]);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run on mount and whenever the active locale changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, [activeLocale]);

  const statusLabel = (status: TaskStatus): string => {
    const meta = STATUS_META[status];
    return meta ? t(meta.labelKey, meta.label) : status;
  };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return tasks.filter((task) => {
      if (statusFacet !== 'all' && task.status !== statusFacet) return false;
      if (!q) return true;
      return (
        task.title.toLowerCase().includes(q) ||
        (task.body ?? '').toLowerCase().includes(q) ||
        (task.entity_name ?? '').toLowerCase().includes(q)
      );
    });
  }, [tasks, filter, statusFacet]);

  const facets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.mta_tasks.all', 'All') },
    ...STATUS_ORDER.map((s) => ({ key: s, label: statusLabel(s) })),
  ];

  // Presentation-only counts derived from the already-loaded rows.
  const stats = useMemo(() => {
    const count = (s: TaskStatus) => tasks.filter((task) => task.status === s).length;
    return { total: tasks.length, open: count('OPEN'), inProgress: count('IN_PROGRESS'), done: count('DONE') };
  }, [tasks]);

  const sourceText = (task: MobileTaskRow): string | null =>
    task.entity_name ? `${task.entity_name}${task.record_id ? `#${task.record_id}` : ''}` : null;

  const subtitle = (task: MobileTaskRow): string => {
    const parts: string[] = [];
    const src = sourceText(task);
    if (src) parts.push(src);
    if (task.due_date) parts.push(t('mobile.mta_tasks.due_prefix', 'Due {{date}}', { date: fmtDate(task.due_date) }));
    return parts.length ? parts.join(' · ') : t('mobile.mta_tasks.no_source', 'No linked record');
  };

  const setStatus = async (task: MobileTaskRow, next: TaskStatus) => {
    setActionBusy(true);
    setError(null);
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'mta_task', 'mta_task_id', task.mta_task_id, { status: next });
    setActionBusy(false);
    if (e) { setError(e.message); return; }
    setSelected(null);
    void reload();
  };

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.mta_tasks.title', 'Tasks')}
        description={t('mobile.mta_tasks.description', 'Your personal task inbox — track and update assigned work.')}
      />

      <MobileStatStrip
        stats={[
          { value: stats.total, label: t('mobile.mta_tasks.stat_total', 'Total') },
          { value: stats.open, label: t('mobile.mta_tasks.stat_open', 'Open') },
          { value: stats.inProgress, label: t('mobile.mta_tasks.stat_in_progress', 'In progress') },
          { value: stats.done, label: t('mobile.mta_tasks.stat_done', 'Done') },
        ]}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.mta_tasks.search', 'Search tasks')}
          aria-label={t('mobile.mta_tasks.search', 'Search tasks')}
        />
      </div>

      <MobileFilterChips
        chips={facets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.mta_tasks.none', 'No tasks match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((task) => {
            const meta = STATUS_META[task.status];
            return (
              <MobileCardRow
                key={task.mta_task_id}
                lead={<ClipboardList />}
                leadTone={meta?.lead ?? 'slate'}
                title={task.title}
                subtitle={subtitle(task)}
                trailing={<MobileStatusPill tone={meta?.pill ?? 'info'}>{statusLabel(task.status)}</MobileStatusPill>}
                chevron
                onClick={() => setSelected(task)}
              />
            );
          })}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelected(null); }} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.mta_tasks.detail', 'Task detail')}>
        <div className="m-grabber" />
        {selected && (() => {
          const meta = STATUS_META[selected.status];
          const src = sourceText(selected);
          const isDone = selected.status === 'DONE';
          const isCancelled = selected.status === 'CANCELLED';
          return (
            <>
              <div className="m-sheet-id">
                <span className="m-av"><ClipboardList size={20} /></span>
                <span>
                  <div className="m-nm">{selected.title}</div>
                  <div className="m-em">{src ?? t('mobile.mta_tasks.no_source', 'No linked record')}</div>
                </span>
              </div>
              <MobileContentCard>
                <MobileDetailRow
                  label={t('mobile.mta_tasks.status_label', 'Status')}
                  value={<MobileStatusPill tone={meta?.pill ?? 'info'}>{statusLabel(selected.status)}</MobileStatusPill>}
                />
                {selected.body && (
                  <MobileDetailRow label={t('mobile.mta_tasks.body', 'Detail')} value={selected.body} />
                )}
                <MobileDetailRow label={t('mobile.mta_tasks.due', 'Due')} value={selected.due_date ? fmtDate(selected.due_date) : t('common.none', 'None')} />
                <MobileDetailRow label={t('mobile.mta_tasks.source', 'Source')} value={src ?? t('common.none', 'None')} />
                <MobileDetailRow label={t('mobile.mta_tasks.created', 'Created')} value={fmtDateTime(selected.created_at)} />
              </MobileContentCard>
              <div className="m-actionbar">
                <button
                  type="button"
                  className="m-btn m-btn-primary"
                  disabled={actionBusy || isDone}
                  onClick={canChange ? (() => void setStatus(selected, 'DONE')) : undefined}
                >
                  <Check size={16} />
                  {t('mobile.mta_tasks.mark_done', 'Mark done')}
                </button>
                <button
                  type="button"
                  className="m-btn m-btn-ghost"
                  disabled={actionBusy || selected.status === 'IN_PROGRESS' || isDone || isCancelled}
                  onClick={canChange ? (() => void setStatus(selected, 'IN_PROGRESS')) : undefined}
                >
                  <Play size={16} />
                  {t('mobile.mta_tasks.start', 'Start')}
                </button>
                <button
                  type="button"
                  className="m-btn m-btn-danger"
                  disabled={actionBusy || isDone || isCancelled}
                  onClick={canChange ? (() => void setStatus(selected, 'CANCELLED')) : undefined}
                >
                  <Ban size={16} />
                  {t('mobile.mta_tasks.cancel_task', 'Cancel')}
                </button>
                <button
                  type="button"
                  className="m-btn m-btn-ghost"
                  disabled={actionBusy}
                  onClick={() => setSelected(null)}
                  aria-label={t('mobile.close', 'Close')}
                >
                  <X size={16} />
                </button>
              </div>
            </>
          );
        })()}
      </div>
    </section>
  );
}
